import React, { useState } from 'react';
import classNames from 'classnames';
import { Disclosure } from '@headlessui/react';
import { motion } from 'framer-motion';
import Title from '../../Atoms/Title';
import Text from '../../Atoms/Text';
import Icon from '../../Atoms/Icon';

interface AccordionElement {
  title: string;
  children: string | string[] | JSX.Element | JSX.Element[];
  subtitle?: string;
}

export interface Props {
  elements: AccordionElement[];
  className?: string;
  scrollToActiveItem?: boolean;
  scrollOffset?: number;
}

const DURATION = 0.3;
const SCROLL_DELAY = DURATION * 1000 + 100;

const variantsDesktop = {
  open: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: DURATION
    }
  },
  closed: {
    opacity: 0,
    height: 0,
    transition: {
      duration: DURATION
    }
  }
};

function Accordion({
  elements,
  className,
  scrollToActiveItem = false,
  scrollOffset = 0
}: Props) {
  const [isActive, setIsActive] = useState(0);

  function handleClick(
    id: number,
    currentTarget: HTMLElement
  ) {
    if (id !== isActive) {
      setIsActive(id);

      if (scrollToActiveItem) {
        setTimeout(() => {
          window.scrollTo({
            top:
              currentTarget.getBoundingClientRect().top +
              window.scrollY -
              scrollOffset,
            left: 0,
            behavior: 'smooth'
          });
        }, SCROLL_DELAY);
      }
    } else {
      setIsActive(-1);
    }
  }
  return (
    <div
      className={classNames(['flex flex-col', className])}
    >
      {elements.map((element) => (
        <Disclosure key={elements.indexOf(element)}>
          <Disclosure.Button
            onClick={(e: { currentTarget: HTMLElement }) =>
              handleClick(
                elements.indexOf(element),
                e.currentTarget
              )
            }
            onKeyDown={(event: {
              key: string;
              currentTarget: HTMLElement;
            }) => {
              if (event.key === 'Enter') {
                handleClick(
                  elements.indexOf(element),
                  event.currentTarget
                );
              }
            }}
            className={classNames([
              'flex flex-row items-center justify-between gap-7 border-sl-gray py-5 text-sl-blue duration-300 md:border-t-[1px] md:px-7 lg:hover:bg-sl-indigo/25'
            ])}
          >
            <div className="flex flex-col items-start justify-start text-left">
              <Title as="h2" size={4} color="inherit">
                {element.title}
              </Title>
              {element.subtitle && (
                <Text font="circular" color="indigo">
                  {element.subtitle}
                </Text>
              )}
            </div>
            <Icon
              icon="chevron-down"
              className={`${
                isActive === elements.indexOf(element)
                  ? 'rotate-0'
                  : 'rotate-90'
              } text-lg duration-300 lg:text-2xl`}
            />
          </Disclosure.Button>
          <motion.div
            variants={variantsDesktop}
            animate={
              isActive === elements.indexOf(element)
                ? 'open'
                : 'closed'
            }
            className="overflow-hidden"
          >
            <div className="pb-7 pt-0 md:p-7">
              <div>{element.children}</div>
            </div>
          </motion.div>
        </Disclosure>
      ))}
    </div>
  );
}

export default Accordion;
